import { DashboardAnalysis } from '@modules/dashboard/components/DashboardAnalysis';
import { useCellDropEventHandlers } from '@modules/dashboard/components/Layout/DashboardCell/hooks';
import { DashboardCellResizer } from '@modules/dashboard/components/Layout/DashboardCellResizer';
import { TextWidget } from '@modules/dashboard/components/TextWidget';
import { stopAutoscroll } from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DragIndicator } from '@mui/icons-material';
import { Box, BoxProps, Skeleton, Stack, Tooltip } from '@mui/material';
import {
  DashboardCell as DashboardCellType,
  DashboardRow,
} from 'generated/graphql';
import { MouseEvent, useMemo, useState } from 'react';

interface DashboardCellProps {
  id: string;
  row: DashboardRow;
  rowHeight: number;
  cell: DashboardCellType;
  sx: BoxProps['sx'];
  onMouseDown?: (event: MouseEvent<HTMLDivElement>) => void;
  isLastCell: boolean;
  showDropIndicator?: boolean;
  shouldDisableInteraction?: boolean;
}

export function DashboardCell({
  id,
  sx,
  row,
  rowHeight,
  cell,
  onMouseDown,
  isLastCell = false,
  showDropIndicator = false,
  shouldDisableInteraction = false,
}: DashboardCellProps) {
  const [shouldShowDragHandle, setShouldShowDragHandle] = useState(false);
  const [shouldEnableDrag, setShouldEnableDrag] = useState(false);
  const [isDragInProgress, setIsDragInProgress] = useState(false);
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const { onDragOver, onDrop } = useCellDropEventHandlers({
    cellID: id,
    rowID: row.id,
  });

  function onDragStart(eve: React.DragEvent) {
    setIsDragInProgress(true);
    eve.dataTransfer.effectAllowed = 'move';
    eve.dataTransfer.setData(
      'application/json',
      JSON.stringify({ cellID: id, rowID: row.id }),
    );

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        dragItemData: {
          cellID: id,
          rowID: row.id,
        },
      },
    });
  }

  function onDragEnd(eve: React.DragEvent) {
    eve.preventDefault();
    setIsDragInProgress(false);

    stopAutoscroll();

    setTimeout(() => {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          dragItemData: null,
          dropTargetData: null,
        },
      });
    }, 100);
  }

  function onDragLeave() {
    setTimeout(() => {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          dropTargetData: null,
        },
      });
    }, 100);
  }

  const widgetObject = dashboard?.widgets?.find(
    (widget) => widget.id === cell.contentID,
  );

  // Memo is required to improve the scroll experience (kills rerender on scroll)
  const cellContent = useMemo(() => {
    const enableDrag = shouldDisableInteraction
      ? undefined
      : () => {
          setShouldEnableDrag(true);
          setShouldShowDragHandle(true);
        };

    const disableDrag = shouldDisableInteraction
      ? undefined
      : () => setShouldEnableDrag(false);

    if (widgetObject?.analysis) {
      return (
        <DashboardAnalysis
          rowID={row.id}
          analysis={widgetObject.analysis}
          shouldDisableInteraction={false}
          onHeaderMouseEnter={enableDrag}
          onHeaderMouseLeave={() => {
            disableDrag?.();
            // Drag handle should not be present when the header is out of focus
            setShouldShowDragHandle(false);
          }}
        />
      );
    }

    if (widgetObject?.textWidget) {
      return (
        <TextWidget
          rowID={row.id}
          cell={cell}
          content={widgetObject.textWidget.text}
          onFocus={() => {
            disableDrag?.();
            // Drag handle should be present when the text editor is focused
            setShouldShowDragHandle(true);
          }}
          onBlur={() => {
            setShouldShowDragHandle(false);
          }}
        />
      );
    }

    return <Skeleton variant="rounded" width="100%" height="100%" />;
  }, [widgetObject, row.id, cell, shouldDisableInteraction]);

  const isTextContent = !!widgetObject?.textWidget;
  const dragHandle = shouldDisableInteraction ? null : (
    <Tooltip arrow title="Drag to Move" placement="top">
      <Box
        className="e-drag-handle"
        sx={{
          cursor: 'grab',
          position: 'absolute',
          left: isTextContent ? '-2px' : 0,
          top: isTextContent ? 4 : 12,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          opacity: shouldShowDragHandle || shouldEnableDrag ? 1 : 0,
          paddingTop: isTextContent ? '4px' : undefined,
          transition: 'opacity .25s ease',
        }}
        onMouseEnter={() => setShouldEnableDrag(true)}
        onMouseLeave={() => setShouldEnableDrag(false)}
      >
        <DragIndicator sx={{ fontSize: '14px' }} />
      </Box>
    </Tooltip>
  );

  const dragIndicator = shouldDisableInteraction ? null : (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: isDragInProgress ? 'block' : 'none',
        background: 'grey',
        opacity: 0.5,
      }}
    />
  );

  return (
    <Stack
      className="cell-container"
      direction="row"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <Box
        className="cell"
        id={id}
        sx={{
          ...sx,
          height: rowHeight,
          position: 'relative',
          cursor: shouldEnableDrag ? 'grab' : undefined,
          '&:hover': {
            '.e-drag-handle': {
              opacity: 1,
            },
          },
        }}
        draggable={shouldEnableDrag}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {dragHandle}
        {dragIndicator}
        {cellContent}
      </Box>
      <DashboardCellResizer
        rowID={row.id}
        rowHeight={rowHeight}
        onMouseDown={onMouseDown}
        skipInteractions={isLastCell || shouldDisableInteraction}
        showDropIndicator={showDropIndicator}
        sx={{
          position: isLastCell ? 'absolute' : 'static',
          right: isLastCell ? '-10px' : '',
        }}
      />
    </Stack>
  );
}
